import {
  App,
  Button,
  Empty,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { academicsApi, type ExamTypeWeightage } from '@/api/academics';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  examTypeId: number;
  weightagePct: number;
}

export function WeightagesPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('ACADEMICS:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('ACADEMICS:DELETE'));

  const [classId, setClassId] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState<ExamTypeWeightage | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: classes } = useQuery({
    queryKey: ['setup', 'classes'],
    queryFn: setupApi.listClasses,
    staleTime: 5 * 60_000,
  });

  const { data: examTypes } = useQuery({
    queryKey: ['setup', 'lookups', 'exam-types', 'active'],
    queryFn: () => setupApi.listLookup('exam-types', true),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['academics', 'weightages', classId],
    queryFn: () => academicsApi.listWeightages(classId!),
    enabled: classId != null,
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) =>
      editing
        ? academicsApi.updateWeightage(editing.id, {
            classId: editing.classId,
            examTypeId: editing.examTypeId,
            weightagePct: Number(v.weightagePct).toFixed(2),
          })
        : academicsApi.createWeightage({
            classId: classId!,
            examTypeId: v.examTypeId,
            weightagePct: Number(v.weightagePct).toFixed(2),
          }),
    onSuccess: () => {
      message.success(editing ? 'Weightage updated' : 'Weightage added');
      qc.invalidateQueries({ queryKey: ['academics', 'weightages', classId] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      academicsApi.toggleWeightage(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['academics', 'weightages', classId] }),
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => academicsApi.deleteWeightage(id),
    onSuccess: () => {
      message.success('Weightage removed');
      qc.invalidateQueries({ queryKey: ['academics', 'weightages', classId] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
  };
  const openEdit = (row: ExamTypeWeightage) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({ examTypeId: row.examTypeId, weightagePct: Number(row.weightagePct) });
  };
  const open = creating || editing != null;

  const usedTypeIds = new Set((data ?? []).map((w) => w.examTypeId));
  const examTypeOptions = (examTypes ?? [])
    .filter((t) => !editing && !usedTypeIds.has(t.id))
    .map((t) => ({ value: t.id, label: t.name }));

  const totalActive = (data ?? [])
    .filter((w) => w.active)
    .reduce((sum, w) => sum + Number(w.weightagePct), 0);

  const columns: TableProps<ExamTypeWeightage>['columns'] = [
    { title: 'Exam type', dataIndex: 'examTypeName' },
    {
      title: 'Weight %',
      dataIndex: 'weightagePct',
      width: 120,
      align: 'right',
      render: (v: string) => `${Number(v).toFixed(2)}%`,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 160,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
          {canDelete && (
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                modal.confirm({
                  title: `Remove ${row.examTypeName}?`,
                  okType: 'danger',
                  onOk: () => remove.mutate(row.id),
                })
              }
            />
          )}
        </Space>
      ),
    },
  ];

  const totalDelta = totalActive - 100;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Exam weightages"
        description="Per-class weightage of each exam type. Active rows must total 100% before results can be calculated."
        actions={
          canCreate && classId != null ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add weightage
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Pick a class"
          style={{ minWidth: 280 }}
          value={classId}
          onChange={(v) => setClassId(v)}
          options={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        {classId != null && data && data.length > 0 && (
          <Tag
            color={totalActive === 100 ? 'success' : 'warning'}
            icon={totalActive === 100 ? <CheckCircleOutlined /> : <WarningOutlined />}
            style={{ fontSize: 13 }}
          >
            Active total: {totalActive.toFixed(2)}%
            {totalActive !== 100 && ` (${totalDelta > 0 ? '+' : ''}${totalDelta.toFixed(2)} from 100)`}
          </Tag>
        )}
      </Space>

      {classId == null ? (
        <Empty description="Pick a class to manage its weightages" />
      ) : data && data.length === 0 && !isLoading ? (
        <Empty description="No weightages defined for this class yet" />
      ) : (
        <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />
      )}

      <Modal
        title={editing ? `Edit · ${editing.examTypeName}` : 'Add exam weightage'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            {!editing && (
              <Form.Item label="Exam type" name="examTypeId" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={examTypeOptions}
                  notFoundContent={<Typography.Text type="secondary">All exam types already have a weightage for this class.</Typography.Text>}
                />
              </Form.Item>
            )}
            <Form.Item
              label="Weight (%)"
              name="weightagePct"
              rules={[{ required: true, type: 'number', min: 0.01, max: 100 }]}
            >
              <InputNumber style={{ width: '100%' }} min={0.01} max={100} step={5} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
